/**
 * Command presets — per-user naming schemes for the canonical command table.
 *
 * A preset only changes WHICH name is primary (and suggested first) for each
 * canonical action; every built-in family name always resolves regardless of
 * the active preset (docs/architecture/command-preset-system.md, §1).
 *
 * This store is user preference state (persisted), NOT document state: two
 * users sharing a .yap file may run different presets over the same actions.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import {
    CANONICAL_COMMANDS,
    normalizeCommandName,
    type CanonicalCommand,
} from '../core/commands/canonicalCommands';

export type CommandFamilyName = '2d' | '3d' | 'default';

export interface CommandPreset {
    id: string;
    name: string;
    isBuiltIn: boolean;
    /**
     * canonicalId -> primary-first active name list. Missing entries fall
     * back to the family pool; this map only ADDS/RENAMES, never deletes.
     */
    overrides: Record<string, string[]>;
    /** Suggestion ordering: which family's names lead autocomplete. */
    suggestionPriority: Array<'2d' | '3d'>;
    /** Fallback family pool used when an override is absent. */
    baseFamily: '2d' | '3d';
    notes?: string;
}

export const PRESET_2D: CommandPreset = {
    id: 'builtin.2d',
    name: '2D Ready',
    isBuiltIn: true,
    overrides: {},
    suggestionPriority: ['2d', '3d'],
    baseFamily: '2d',
    notes: 'Classic 2D spelling: LINE/L, PLINE/PL, CIRCLE/C ...',
};

export const PRESET_3D: CommandPreset = {
    id: 'builtin.3d',
    name: '3D Ready',
    isBuiltIn: true,
    overrides: {},
    suggestionPriority: ['3d', '2d'],
    baseFamily: '3d',
    notes: '3D spelling: _LINE, _POLYLINE, _CIRCLE ...',
};

export const PRESET_DEFAULT: CommandPreset = {
    id: 'builtin.default',
    name: 'Yap Default',
    isBuiltIn: true,
    overrides: {},
    suggestionPriority: ['2d', '3d'],
    baseFamily: '2d',
    notes: 'Balanced defaults — 2D spelling leads, 3D aliases resolve.',
};

const BUILT_INS = [PRESET_DEFAULT, PRESET_2D, PRESET_3D];

interface CommandPresetState {
    presets: CommandPreset[];
    activePresetId: string;

    setActivePreset: (id: string) => void;
    createPreset: (name: string, fromActive?: boolean) => CommandPreset;
    duplicatePreset: (id: string, name: string) => CommandPreset | null;
    renamePreset: (id: string, name: string) => void;
    deletePreset: (id: string) => void;
    /** Set (or clear with empty list) the active names for one action. */
    setOverride: (presetId: string, canonicalId: string, names: string[]) => void;
    resetPreset: (id: string) => void;
    importPreset: (json: string) => { ok: boolean; message: string };
    exportPreset: (id: string) => string | null;
    /**
     * Create or wholesale-replace a named import preset (fixed id) and make
     * it active. Used by file imports (Rhino options ini) so re-importing
     * updates one preset in place instead of piling up duplicates.
     */
    upsertImportedPreset: (input: {
        id: string;
        name: string;
        overrides: Record<string, string[]>;
        suggestionPriority: Array<'2d' | '3d'>;
        baseFamily: '2d' | '3d';
        notes?: string;
    }) => { presetId: string; created: boolean; commandCount: number };
}

const clone = (p: CommandPreset): CommandPreset => ({
    ...p,
    overrides: JSON.parse(JSON.stringify(p.overrides ?? {})),
    suggestionPriority: [...p.suggestionPriority],
});

export const useCommandPresetStore = create<CommandPresetState>()(
    persist(
        (set, get) => ({
            presets: BUILT_INS.map(clone),
            activePresetId: PRESET_DEFAULT.id,

            setActivePreset: (id) => {
                if (get().presets.some((p) => p.id === id)) set({ activePresetId: id });
            },

            createPreset: (name, fromActive = true) => {
                const base = fromActive
                    ? clone(get().presets.find((p) => p.id === get().activePresetId) ?? PRESET_DEFAULT)
                    : clone(PRESET_DEFAULT);
                const preset: CommandPreset = {
                    ...base,
                    id: uuidv4(),
                    name: name.trim() || 'Custom Preset',
                    isBuiltIn: false,
                };
                set((s) => ({ presets: [...s.presets, preset], activePresetId: preset.id }));
                return preset;
            },

            duplicatePreset: (id, name) => {
                const source = get().presets.find((p) => p.id === id);
                if (!source) return null;
                const preset: CommandPreset = {
                    ...clone(source),
                    id: uuidv4(),
                    name: name.trim() || `${source.name} Copy`,
                    isBuiltIn: false,
                };
                set((s) => ({ presets: [...s.presets, preset] }));
                return preset;
            },

            renamePreset: (id, name) => {
                set((s) => ({
                    presets: s.presets.map((p) =>
                        p.id === id && !p.isBuiltIn ? { ...p, name: name.trim() || p.name } : p
                    ),
                }));
            },

            deletePreset: (id) => {
                const target = get().presets.find((p) => p.id === id);
                if (!target || target.isBuiltIn) return;
                set((s) => ({
                    presets: s.presets.filter((p) => p.id !== id),
                    activePresetId:
                        s.activePresetId === id ? PRESET_DEFAULT.id : s.activePresetId,
                }));
            },

            setOverride: (presetId, canonicalId, names) => {
                set((s) => ({
                    presets: s.presets.map((p) => {
                        if (p.id !== presetId) return p;
                        const overrides = { ...p.overrides };
                        const cleaned = names.map((n) => n.trim()).filter(Boolean);
                        if (cleaned.length === 0) delete overrides[canonicalId];
                        else overrides[canonicalId] = cleaned;
                        return { ...p, overrides };
                    }),
                }));
            },

            resetPreset: (id) => {
                const builtIn = BUILT_INS.find((p) => p.id === id);
                if (!builtIn) return;
                set((s) => ({
                    presets: s.presets.map((p) => (p.id === id ? clone(builtIn) : p)),
                }));
            },

            importPreset: (json) => {
                try {
                    const raw = JSON.parse(json);
                    const name = typeof raw?.name === 'string' && raw.name.trim() ? raw.name.trim() : null;
                    if (!name) return { ok: false, message: 'Preset must have a "name" field.' };
                    const overrides: Record<string, string[]> = {};
                    if (raw.overrides && typeof raw.overrides === 'object') {
                        const known = new Set(CANONICAL_COMMANDS.map((c) => c.id));
                        for (const [key, value] of Object.entries(raw.overrides)) {
                            // Drop unknown canonical ids silently but report the count.
                            if (!known.has(key)) continue;
                            if (Array.isArray(value)) {
                                const names = value.filter((v): v is string => typeof v === 'string' && !!v.trim());
                                if (names.length > 0) overrides[key] = names;
                            }
                        }
                    }
                    const preset: CommandPreset = {
                        id: uuidv4(),
                        name,
                        isBuiltIn: false,
                        overrides,
                        suggestionPriority:
                            Array.isArray(raw.suggestionPriority) && raw.suggestionPriority.length > 0
                                ? raw.suggestionPriority
                                      .filter((f: unknown) => f === '2d' || f === '3d' || f === 'cad' || f === 'autocad' || f === 'rhino')
                                      .map((f: string) => (f === 'rhino' ? '3d' : '2d'))
                                : ['2d', '3d'],
                        baseFamily: raw.baseFamily === 'rhino' || raw.baseFamily === '3d' ? '3d' : '2d',
                        notes: typeof raw.notes === 'string' ? raw.notes : undefined,
                    };
                    set((s) => ({ presets: [...s.presets, preset] }));
                    return { ok: true, message: `Imported "${name}" (${Object.keys(overrides).length} overrides).` };
                } catch {
                    return { ok: false, message: 'Invalid JSON preset.' };
                }
            },

            exportPreset: (id) => {
                const preset = get().presets.find((p) => p.id === id);
                if (!preset) return null;
                return JSON.stringify(
                    {
                        format: 'yap-command-preset',
                        version: 1,
                        name: preset.name,
                        suggestionPriority: preset.suggestionPriority,
                        baseFamily: preset.baseFamily,
                        notes: preset.notes,
                        overrides: preset.overrides,
                    },
                    null,
                    2
                );
            },

            upsertImportedPreset: (input) => {
                // Only names of known canonical commands can resolve; unknown
                // ids are dropped exactly like importPreset does.
                const known = new Set(CANONICAL_COMMANDS.map((c) => c.id));
                const overrides: Record<string, string[]> = {};
                for (const [canonicalId, names] of Object.entries(input.overrides)) {
                    if (!known.has(canonicalId)) continue;
                    const seen = new Set<string>();
                    const cleaned = names
                        .map((n) => n.trim())
                        .filter((n) => {
                            if (!n) return false;
                            const key = normalizeCommandName(n);
                            if (seen.has(key)) return false;
                            seen.add(key);
                            return true;
                        });
                    if (cleaned.length > 0) overrides[canonicalId] = cleaned;
                }
                const commandCount = Object.keys(overrides).length;

                const existing = get().presets.find((p) => p.id === input.id);
                if (existing && !existing.isBuiltIn) {
                    set((s) => ({
                        presets: s.presets.map((p) =>
                            p.id === input.id
                                ? {
                                      ...p,
                                      name: input.name,
                                      overrides,
                                      suggestionPriority: [...input.suggestionPriority],
                                      baseFamily: input.baseFamily,
                                      notes: input.notes,
                                  }
                                : p
                        ),
                        activePresetId: input.id,
                    }));
                    return { presetId: input.id, created: false, commandCount };
                }

                const preset: CommandPreset = {
                    id: input.id,
                    name: input.name,
                    isBuiltIn: false,
                    overrides,
                    suggestionPriority: [...input.suggestionPriority],
                    baseFamily: input.baseFamily,
                    notes: input.notes,
                };
                set((s) => ({ presets: [...s.presets, preset], activePresetId: preset.id }));
                return { presetId: preset.id, created: true, commandCount };
            },
        }),
        {
            name: 'yap-command-presets',
            // Migration: persisted presets may still carry the pre-rename
            // family ids (pre-rename values) and built-in ids. Remap them so
            // existing user preferences survive the rename.
            merge: (persisted, current) => {
                const p = (persisted ?? {}) as Partial<CommandPresetState>;
                const remapFamily = (f: string): '2d' | '3d' => (f === 'rhino' || f === '3d' ? '3d' : '2d');
                const remapId = (id: string): string =>
                    id === 'builtin.autocad' ? 'builtin.2d' : id === 'builtin.rhino' ? 'builtin.3d' : id;
                const presets = (p.presets ?? current.presets).map((preset) => ({
                    ...preset,
                    id: remapId(preset.id),
                    suggestionPriority: (preset.suggestionPriority ?? []).map((f) =>
                        remapFamily(String(f))
                    ) as CommandPreset['suggestionPriority'],
                    baseFamily: remapFamily(String(preset.baseFamily)),
                }));
                return {
                    ...current,
                    ...p,
                    presets,
                    activePresetId: p.activePresetId ? remapId(p.activePresetId) : current.activePresetId,
                };
            },
        }
    )
);

// ============================================================================
// Resolver — pure functions over the store, consumed by the dispatcher
// ============================================================================

/** Effective active names for one command under a preset: override first,
 *  then base-family pool, then the other family pool. Built-in names are
 *  ALWAYS appended (deduped) so no callable spelling is ever lost. */
export function effectiveNames(preset: CommandPreset, cmd: CanonicalCommand): string[] {
    const builtIn = [...cmd.names['2d'], ...cmd.names['3d']];
    const override = preset.overrides[cmd.id];
    if (!override || override.length === 0) {
        const [first, second] = preset.suggestionPriority;
        return [...cmd.names[first], ...cmd.names[second]];
    }
    const overrideKeys = new Set(override.map(normalizeCommandName));
    const appended = builtIn.filter((n) => !overrideKeys.has(normalizeCommandName(n)));
    return [...override, ...appended];
}

/** All canonical commands indexed by normalized callable name -> {cmd, isPrimary}.
 *  Two-pass registration: override names (edited/imported aliases) claim
 *  their command first, then every effective built-in name follows in table
 *  order. Overrides pre-claim so an imported alias always beats the same
 *  spelling used as another command's built-in name (the imported layout is
 *  the intent — mirroring the keyboard collision rule); first writer of a
 *  name within a pass wins as primary (used for suggestion labels only;
 *  dispatch always resolves via this map, so every listed name works). */
export function buildNameIndex(preset: CommandPreset): Map<string, CanonicalCommand> {
    const index = new Map<string, CanonicalCommand>();
    // Pass 1 — override names.
    for (const cmd of CANONICAL_COMMANDS) {
        for (const name of preset.overrides[cmd.id] ?? []) {
            const key = normalizeCommandName(name);
            if (key && !index.has(key)) index.set(key, cmd);
        }
    }
    // Pass 2 — override-first effective name lists (override names already
    // registered in pass 1 are skipped by the has() guard).
    for (const cmd of CANONICAL_COMMANDS) {
        for (const name of effectiveNames(preset, cmd)) {
            const key = normalizeCommandName(name);
            if (!index.has(key)) index.set(key, cmd);
        }
    }
    return index;
}
